import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { UserMenu } from './UserMenu'
import { IdleWarningDialog } from './IdleWarningDialog'
import { Sidebar } from './Sidebar'
import { isDemoMode } from '@/lib/auth'
import { ReferenceDataProvider } from '@/lib/reference/ReferenceProvider'

const COLLAPSED_KEY = 'omc.nav.collapsed'

function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Authenticated shell: a left navigation rail, a thin top bar for identity,
 * and the routed screen. The Gantt will live in the same frame, so the rail
 * collapses and the content area is not width-capped — each screen decides
 * how wide it wants to be.
 */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    try {
      sessionStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      // Private mode: the rail simply reopens expanded next time.
    }
  }, [collapsed])

  // Route change closes the mobile drawer; Escape does too.
  useEffect(() => setDrawerOpen(false), [pathname])
  useEffect(() => {
    if (!drawerOpen) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  return (
    <ReferenceDataProvider>
      <div className="flex h-full flex-col bg-canvas">
        {isDemoMode() && (
          <div className="shrink-0 bg-surface-dark px-6 py-2 text-center">
            <p className="t-caption text-on-dark-soft">Demo</p>
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          <div className="hidden shrink-0 lg:block">
            <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed((v) => !v)} />
          </div>

          {drawerOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <button
                type="button"
                aria-label="Close navigation"
                onClick={() => setDrawerOpen(false)}
                className="absolute inset-0 bg-surface-dark/40"
              />
              <div className="absolute inset-y-0 left-0">
                <Sidebar
                  collapsed={false}
                  onToggleCollapsed={() => setCollapsed((v) => !v)}
                  onNavigate={() => setDrawerOpen(false)}
                />
              </div>
            </div>
          )}

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-between
                               gap-4 border-b border-hairline bg-canvas px-4 sm:px-6">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation"
                aria-expanded={drawerOpen}
                className="-ml-1 rounded-[12px] p-2 text-body transition-colors
                           hover:bg-surface-soft active:bg-surface-strong lg:hidden"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path
                    d="M3 5.5h14M3 10h14M3 14.5h14"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <div className="ml-auto">
                <UserMenu />
              </div>
            </header>

            <main className="min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-6 sm:py-10">
              <Outlet />
            </main>
          </div>
        </div>

        <IdleWarningDialog />
      </div>
    </ReferenceDataProvider>
  )
}
